const ANGLE_RAD_90 = 90 * Math.PI / 180;
const ANGLE_RAD_180 = 180 * Math.PI / 180;

export function drawPathPortion(start, end, ctx, color = null) {

  const canvasCenter = { x: ctx.canvas.width / 2, y: ctx.canvas.height / 2 }

  const startX = start.x + canvasCenter.x;
  const startY = start.y + canvasCenter.y;
  const endX = end.x + canvasCenter.x;
  const endY = end.y + canvasCenter.y;

  const startP1x = start.p1x + canvasCenter.x;
  const startP1y = start.p1y + canvasCenter.y;

  const startP2x = start.p2x + canvasCenter.x;
  const startP2y = start.p2y + canvasCenter.y;

  const endP1x = end.p1x + canvasCenter.x;
  const endP1y = end.p1y + canvasCenter.y;

  const endP2x = end.p2x + canvasCenter.x;
  const endP2y = end.p2y + canvasCenter.y;

  if (color) {
    ctx.fillStyle = color;
  } else {
    const h = end.color.h;
    const s = end.color.s * 100;
    const l = end.color.l * 100;
    ctx.fillStyle = "hsl(" + h + "," + s + "%," + l + "%)";
  }

  ctx.beginPath();
  ctx.moveTo(startP1x, startP1y);
  ctx.lineTo(startP2x, startP2y);
  ctx.lineTo(endP2x, endP2y);
  ctx.lineTo(endP1x, endP1y);
  ctx.closePath();
  ctx.fill();
}

export function curveThroughPoints(points, ctx) {
  let i, n, a, b, x, y;
  for (i = 1, n = points.length - 2; i < n; i++) {
    a = points[i];
    b = points[i + 1];
    x = (a.x + b.x) * 0.5;
    y = (a.y + b.y) * 0.5;
    ctx.quadraticCurveTo(a.x, a.y, x, y);
  }
  a = points[i];
  b = points[i + 1];
  ctx.quadraticCurveTo(a.x, a.y, b.x, b.y);
}

export function drawRoundRect(ctx, x, y, w, h, r) {
  if (w < 2 * r) r = w / 2;
  if (h < 2 * r) r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  return ctx;
}

export function getColorDifference(hexColor1, hexColor2) {
  const rgb1 = hexToRgb(hexColor1);
  const rgb2 = hexToRgb(hexColor2);
  const lab1 = rgb2lab(rgb1[0], rgb1[1], rgb1[2]);
  const lab2 = rgb2lab(rgb2[0], rgb2[1], rgb2[2]);
  return Math.sqrt(Math.pow(lab2[0] - lab1[0], 2) + Math.pow(lab2[1] - lab1[1], 2) + Math.pow(lab2[2] - lab1[2], 2));
}


export function isColorDark(hexColor) {
  const rgb = hexToRgb(hexColor);
  return isRgbColorDark(rgb[0], rgb[1], rgb[2]);
}

export function isRgbColorDark(r, g, b) {
  // HSP (Highly Sensitive Perceived brightness) equation from http://alienryderflex.com/hsp.html
  const hsp = getHsp(r, g, b);
  // Using the HSP value, determine whether the color is light or dark
  if (hsp > 132) {
    return false;
  } else {
    return true;
  }
}

export function getHsp(r, g, b) {
  return Math.sqrt(
    0.299 * (r * r) +
    0.587 * (g * g) +
    0.114 * (b * b)
  );
}

export function getComplementaryColor(color) {
  return isColorDark(color) ? '#ffffff' : '#191919';
}

export function getOverlayColor(color, complementaryColor, averageBackgroundColor = null) {
  if (averageBackgroundColor) {
    let colorDiff = getColorDifference(color, averageBackgroundColor);
    if (colorDiff < 110) {
      color = complementaryColor;
    }
    colorDiff = getColorDifference(color, averageBackgroundColor);
    if (colorDiff < 120) {
      color = '#191919';
    }
    colorDiff = getColorDifference(color, averageBackgroundColor);
    if (colorDiff < 120) {
      color = '#ffffff';
    }
  }
  return color;
}

/**
 * Helper for isValidHexColor_.
 * @type {!RegExp}
 * @private
 */
const validHexColorRe_ = /^#(?:[0-9a-f]{3}){1,2}$/i;

/**
 * Regular expression for extracting the digits in a hex color triplet.
 * @type {!RegExp}
 * @private
 */
const hexTripletRe_ = /#(.)(.)(.)/;


/**
 * Checks if a string is a valid hex color.  We expect strings of the format
 * #RRGGBB (ex: #1b3d5f) or #RGB (ex: #3CA == #33CCAA).
 * @param {string} str String to check.
 * @return {boolean} Whether the string is a valid hex color.
 * @private
 */
function isValidHexColor_(str) {
  return validHexColorRe_.test(str);
};

/**
 * Normalize an hex representation of a color
 * @param {string} hexColor an hex color string.
 * @return {string} hex color in the format '#rrggbb' with all lowercase
 *     literals.
 */
export function normalizeHex(hexColor) {
  if (!isValidHexColor_(hexColor)) {
    throw Error("'" + hexColor + "' is not a valid hex color");
  }
  if (hexColor.length == 4) {  // of the form #RGB
    hexColor = hexColor.replace(hexTripletRe_, '#$1$1$2$2$3$3');
  }
  return hexColor.toLowerCase();
};

/**
 * Converts a hex representation of a color to RGB.
 * @param {string} hexColor Color to convert.
 * @return {!Array} rgb representation of the color.
 */
export function hexToRgb(hexColor) {
  hexColor = normalizeHex(hexColor);
  const rgb = parseInt(hexColor.substr(1), 16);
  const r = rgb >> 16;
  const g = (rgb >> 8) & 255;
  const b = rgb & 255;
  return [r, g, b];
};

/**
 * Converts from RGB values to an array of HSV values.
 * @param {number} red Red value in [0, 255].
 * @param {number} green Green value in [0, 255].
 * @param {number} blue Blue value in [0, 255].
 * @return {!Array} hsv representation of the color.
 */
export function rgbToHsv(red, green, blue) {
  var max = Math.max(Math.max(red, green), blue);
  var min = Math.min(Math.min(red, green), blue);
  var hue;
  var saturation;
  var value = max;
  if (min == max) {
    hue = 0;
    saturation = 0;
  } else {
    var delta = (max - min);
    saturation = delta / max;

    if (red == max) {
      hue = (green - blue) / delta;
    } else if (green == max) {
      hue = 2 + ((blue - red) / delta);
    } else {
      hue = 4 + ((red - green) / delta);
    }
    hue *= 60;
    if (hue < 0) {
      hue += 360;
    }
    if (hue > 360) {
      hue -= 360;
    }
  }

  return [hue, saturation, value];
};

/**
 * Converts a color from RGB color space to HSL color space.
 * Modified from {@link http://en.wikipedia.org/wiki/HLS_color_space}.
 * @param {number} r Value of red, in [0, 255].
 * @param {number} g Value of green, in [0, 255].
 * @param {number} b Value of blue, in [0, 255].
 * @return {!Array} hsl representation of the color.
 */
export function rgbToHsl(r, g, b) {
  'use strict';
  // First must normalize r, g, b to be between 0 and 1.
  var normR = r / 255;
  var normG = g / 255;
  var normB = b / 255;
  var max = Math.max(normR, normG, normB);
  var min = Math.min(normR, normG, normB);
  var h = 0;
  var s = 0;

  // Luminosity is the average of the max and min rgb color intensities.
  var l = 0.5 * (max + min);

  // The hue and saturation are dependent on which color intensity is the max.
  // If max and min are equal, the color is gray and h and s should be 0.
  if (max != min) {
    if (max == normR) {
      h = 60 * (normG - normB) / (max - min);
    } else if (max == normG) {
      h = 60 * (normB - normR) / (max - min) + 120;
    } else if (max == normB) {
      h = 60 * (normR - normG) / (max - min) + 240;
    }

    if (0 < l && l <= 0.5) {
      s = (max - min) / (2 * l);
    } else {
      s = (max - min) / (2 - 2 * l);
    }
  }

  // Make sure the hue falls between 0 and 360.
  return [Math.round(h + 360) % 360, s, l];
};

/**
 * Converts from an array of RGB values to an array of HSV values.
 * @param {Array} rgb rgb representation of the color.
 * @return {!Array} hsv representation of the color.
 */
export function rgbArrayToHsv(rgb) {
  return rgbToHsv(rgb[0], rgb[1], rgb[2]);
};

/**
 * Converts a hex representation of a color to HSV
 * @param {string} hex Color to convert.
 * @return {!Array} hsv representation of the color.
 */
export function hexToHsv(hex) {
  return rgbArrayToHsv(hexToRgb(hex));
};

/**
 * Converts a hex representation of a color to HSL.
 * @param {string} hex Color to convert.
 * @return {!Array} hsl representation of the color.
 */
export function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  return rgbToHsl(rgb[0], rgb[1], rgb[2]);
};

/**
 * Helper for hslToRgb.
 * @param {number} v1 Helper variable 1.
 * @param {number} v2 Helper variable 2.
 * @param {number} vH Helper variable 3.
 * @return {number} Appropriate RGB value, given the above.
 * @private
 */
export function hueToRgb_(v1, v2, vH) {
  'use strict';
  if (vH < 0) {
    vH += 1;
  } else if (vH > 1) {
    vH -= 1;
  }
  if ((6 * vH) < 1) {
    return (v1 + (v2 - v1) * 6 * vH);
  } else if (2 * vH < 1) {
    return v2;
  } else if (3 * vH < 2) {
    return (v1 + (v2 - v1) * ((2 / 3) - vH) * 6);
  }
  return v1;
};

/**
 * Converts a color from HSL color space to RGB color space.
 * Modified from {@link http://www.easyrgb.com/math.html}
 * @param {number} h Hue, in [0, 360].
 * @param {number} s Saturation, in [0, 1].
 * @param {number} l Luminosity, in [0, 1].
 * @return {Array} rgb representation of the color.
 */
export function hslToRgb(h, s, l) {
  'use strict';
  let r = 0;
  let g = 0;
  let b = 0;
  const normH = h / 360;  // normalize h to fall in [0, 1]

  if (s == 0) {
    r = g = b = l * 255;
  } else {
    let temp1 = 0;
    let temp2 = 0;
    if (l < 0.5) {
      temp2 = l * (1 + s);
    } else {
      temp2 = l + s - (s * l);
    }
    temp1 = 2 * l - temp2;
    r = 255 * hueToRgb_(temp1, temp2, normH + (1 / 3));
    g = 255 * hueToRgb_(temp1, temp2, normH);
    b = 255 * hueToRgb_(temp1, temp2, normH - (1 / 3));
  }

  return [Math.round(r), Math.round(g), Math.round(b)];
};

/**
 * Converts a color from RGB to hex representation.
 * @param {number} r Amount of red, int between 0 and 255.
 * @param {number} g Amount of green, int between 0 and 255.
 * @param {number} b Amount of blue, int between 0 and 255.
 * @return {string} hex representation of the color.
 */
export function rgbToHex(r, g, b) {
  r = Number(r);
  g = Number(g);
  b = Number(b);
  if (r != (r & 255) || g != (g & 255) || b != (b & 255)) {
    throw Error('"(' + r + ',' + g + ',' + b + '") is not a valid RGB color');
  }
  const rgb = (r << 16) | (g << 8) | b;
  if (r < 0x10) {
    return '#' + (0x1000000 | rgb).toString(16).substr(1);
  }
  return '#' + rgb.toString(16);
};

/**
 * Converts a color from HSL color space to RGB color space.
 * Modified from {@link http://www.easyrgb.com/math.html}
 * @param {number} h Hue, in [0, 360].
 * @param {number} s Saturation, in [0, 1].
 * @param {number} l Luminosity, in [0, 1].
 * @return {String} hex representation of the color.
 */
export function hslToHex(h, s, l) {
  const rgb = hslToRgb(h, s, l)
  return rgbToHex(rgb[0], rgb[1], rgb[2]);
};

export function rgb2lab(r, g, b) {
  //http://www.brucelindbloom.com

  let fx, fy, fz;
  const eps = 216 / 24389;
  const k = 24389 / 27;

  let xr = 0.964221;  // reference white D50
  let yr = 1.0;
  let zr = 0.825211;

  // RGB to XYZ
  r = r / 255; //R 0..1
  g = g / 255; //G 0..1
  b = b / 255; //B 0..1

  // assuming sRGB (D65)
  if (r <= 0.04045)
    r = r / 12;
  else
    r = Math.pow((r + 0.055) / 1.055, 2.4);

  if (g <= 0.04045)
    g = g / 12;
  else
    g = Math.pow((g + 0.055) / 1.055, 2.4);

  if (b <= 0.04045)
    b = b / 12;
  else
    b = Math.pow((b + 0.055) / 1.055, 2.4);


  const X = 0.436052025 * r + 0.385081593 * g + 0.143087414 * b;
  const Y = 0.222491598 * r + 0.71688606 * g + 0.060621486 * b;
  const Z = 0.013929122 * r + 0.097097002 * g + 0.71418547 * b;

  // XYZ to Lab
  xr = X / xr;
  yr = Y / yr;
  zr = Z / zr;

  if (xr > eps)
    fx = Math.pow(xr, 1 / 3.);
  else
    fx = ((k * xr + 16.) / 116.);

  if (yr > eps)
    fy = Math.pow(yr, 1 / 3.);
  else
    fy = ((k * yr + 16.) / 116.);

  if (zr > eps)
    fz = Math.pow(zr, 1 / 3.);
  else
    fz = ((k * zr + 16.) / 116);

  const Ls = (116 * fy) - 16;
  const as = 500 * (fx - fy);
  const bs = 200 * (fy - fz);

  const lab = [2.55 * Ls + 0.5, as + 0.5, bs + 0.5];
  return lab;
}